import { useQuery } from '@apollo/client'
import { Grid } from '@mui/material'
import { useState } from 'react'
import {
    EducationOptions,
    EducationOptionsQuery,
} from 'recruiter/graphql/candidateBackground.gql'
import UnsavedChangesModal from 'shared/components/UnsavedChangesModal/UnsavedChangesModal'
import UnsavedChangesModalNavBlocker from 'shared/components/UnsavedChangesModal/UnsavedChangesModalNavBlocker'
import { Card, StyledDivider } from '../styled'
import { Degrees } from './Degrees'
import TestScores from './TestScores'

import type {
    GetCandidateProfileForRecruiterQuery,
    GetCandidateProfileForRecruiterQueryVariables,
} from '__generated__/graphql'
import type { FC } from 'react'
import type { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import Miscellaneous from './Miscellaneous'
import PersonalityScores from './PersonalityScores'

interface EducationProps {
    data: GetCandidateProfileForRecruiterQuery
    refetch: ApolloQueryRefetchFunction<
        GetCandidateProfileForRecruiterQuery,
        GetCandidateProfileForRecruiterQueryVariables
    >
}

export enum EDUCATION_SECTIONS {
    DEGREES = 'Degrees',
    TEST_SCORES = 'Test Scores',
    PROGRAMS = 'Programs',
    OTHER_ANALYSTS = 'Other Analysts',
    PERSONALITY_SCORES = 'Personality Scores',
}

const Education: FC<EducationProps> = ({ data, refetch }) => {
    const [edit, setEdit] = useState<EDUCATION_SECTIONS | null>(null)
    const [unsavedChangesExist, setUnsavedChangesExist] =
        useState<boolean>(false)
    const [unsavedChangesModalOpen, setUnsavedChangesModalOpen] =
        useState<boolean>(false)
    const [nextEditSection, setNextEditSection] =
        useState<EDUCATION_SECTIONS | null>(null)
    const { data: educationOptions } = useQuery<EducationOptions>(
        EducationOptionsQuery
    )

    const handleSetEdit = (section) => {
        if (unsavedChangesExist) {
            setNextEditSection(section)
            setUnsavedChangesModalOpen(true)
        } else {
            setEdit(section)
            setNextEditSection(null)
        }
    }

    const closeAfterSave = () => {
        setEdit(null)
        refetch()
        setUnsavedChangesExist(false)
    }

    return (
        <Grid item xs={5}>
            <UnsavedChangesModalNavBlocker
                unsavedChangesExist={unsavedChangesExist}
            />
            <UnsavedChangesModal
                open={unsavedChangesModalOpen}
                onLeaveClick={() => {
                    setUnsavedChangesModalOpen(false)
                    setUnsavedChangesExist(false)
                    setEdit(nextEditSection)
                    setNextEditSection(null)
                }}
                onStayClick={() => {
                    setUnsavedChangesModalOpen(false)
                }}
            />
            <Card>
                <Degrees
                    edit={edit}
                    setEdit={setEdit}
                    setEditWithUnsavedChanges={handleSetEdit}
                    education={data.candidate?.education ?? []}
                    candidateUserId={data.candidate?.user?.id ?? ''}
                    closeAfterSave={closeAfterSave}
                    setUnsavedChangesExist={setUnsavedChangesExist}
                />
                <StyledDivider />
                <TestScores
                    edit={edit}
                    setEdit={setEdit}
                    setEditWithUnsavedChanges={handleSetEdit}
                    testScores={data.candidate?.testScores}
                    candidateId={data.candidate?.id ?? ''}
                    closeAfterSave={closeAfterSave}
                    setUnsavedChangesExist={setUnsavedChangesExist}
                />
                <StyledDivider />
                <PersonalityScores
                    edit={edit}
                    setEdit={setEdit}
                    setEditWithUnsavedChanges={handleSetEdit}
                    closeAfterSave={closeAfterSave}
                    candidateId={data.candidate?.id ?? ''}
                    setUnsavedChangesExist={setUnsavedChangesExist}
                    personalityScores={data.candidate?.personalityScores}
                />
                <StyledDivider />
                <Miscellaneous
                    edit={edit}
                    setEdit={setEdit}
                    setEditWithUnsavedChanges={handleSetEdit}
                    programOptions={educationOptions?.programs || []}
                    programs={data.candidate?.programs ?? []}
                    closeAfterSave={closeAfterSave}
                    candidateId={data.candidate?.id ?? ''}
                    setUnsavedChangesExist={setUnsavedChangesExist}
                    isDeferredMba={data.candidate?.isDeferredMba}
                />
            </Card>
        </Grid>
    )
}

export default Education
